import { useState, useEffect } from "react";
import "@/App.css";
import { Toaster, toast } from 'sonner';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Menu, 
  X, 
  ChevronDown,
  Star,
  Shield,
  Zap,
  Award,
  Leaf,
  Search,
  Wrench,
  CheckCircle,
  ArrowRight,
  Facebook,
  Instagram,
  Linkedin,
  Bug,
  Bird,
  Trash2,
  Droplets,
  TreeDeciduous,
  Rat
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Card, CardContent } from "./components/ui/card";

// Company info
const COMPANY = {
  name: "Hygiène de l'Ourcq",
  shortName: "HDL",
  siren: "378 192 306 Meaux - APE 8129 A",
  phone1: "01.60.25.61.82",
  phone2: "06.74.19.40.39",
  address: "12 Bis rue de Lizy",
  city: "77440 Mary-Sur-Marne",
  email: "philippe.gadi@sfr.fr",
  director: "Philippe Gadi",
  since: "1990",
  logo: "https://customer-assets.emergentagent.com/job_deratisation-expert/artifacts/wx7cjvuc_Hygi%C3%A8ne%20de%20l%E2%80%99Ourcq%20%281%29.jpg"
};

// StaticForms config
const STATICFORMS_CONFIG = {
  url: "https://api.staticforms.xyz/submit",
  accessKey: "sf_eagac55m53aj7m9mk6i3ebk3"
};

// Services data
const SERVICES = [
  {
    id: 1,
    title: "Dératisation",
    subtitle: "Rats & Souris",
    description: "Élimination efficace des rongeurs avec des méthodes professionnelles et sécurisées pour votre habitation ou local.",
    icon: Rat,
  },
  {
    id: 2,
    title: "Désinsectisation",
    subtitle: "Cafards, Punaises, Blattes",
    description: "Traitement complet contre tous types d'insectes nuisibles : cafards, punaises de lit, blattes, fourmis.",
    icon: Bug,
  },
  {
    id: 3,
    title: "Guêpes & Frelons",
    subtitle: "Frelons européens & asiatiques",
    description: "Intervention rapide et sécurisée pour la destruction de nids de guêpes et frelons européens et asiatiques.",
    icon: Bug,
  },
  {
    id: 4,
    title: "Désinfection",
    subtitle: "Locaux & Surfaces",
    description: "Désinfection professionnelle de vos locaux contre virus, bactéries et agents pathogènes.",
    icon: Droplets,
  },
  {
    id: 5,
    title: "Chenilles Processionnaires",
    subtitle: "Pose de pièges",
    description: "Installation de pièges écologiques contre les chenilles processionnaires du pin et du chêne. Protection de vos arbres et de votre famille.",
    icon: TreeDeciduous,
  },
  {
    id: 6,
    title: "Anti-Pigeons",
    subtitle: "Dépigeonnisation",
    description: "Solutions durables contre les pigeons : pics, filets, répulsifs et nettoyage des déjections.",
    icon: Bird,
  },
  {
    id: 7,
    title: "Vente de Produits",
    subtitle: "Collectivités & Particuliers",
    description: "Vente de produits professionnels spécial collectivités accessibles aux particuliers. Conseils personnalisés.",
    icon: Trash2,
  }
];

// Why choose us
const WHY_US = [
  {
    icon: Zap,
    title: "Intervention Rapide",
    description: "Disponibles 7j/7, nous intervenons rapidement pour résoudre vos problèmes de nuisibles."
  },
  {
    icon: Shield,
    title: "Produits Certifiés",
    description: "Utilisation exclusive de produits homologués, respectueux de l'environnement et de votre santé."
  },
  {
    icon: Award,
    title: "Techniciens Qualifiés",
    description: "Notre équipe possède plus de 35 ans d'expérience dans la lutte contre les nuisibles."
  },
  {
    icon: Leaf,
    title: "Solutions Durables",
    description: "Des traitements efficaces avec suivi pour garantir des résultats pérennes."
  }
];

// Pests Info for educational section
const PESTS_INFO = [
  {
    id: "rats",
    name: "Rats & Souris",
    icon: Rat,
    description: "Les rongeurs sont des nuisibles très répandus qui peuvent causer des dégâts importants.",
    signs: ["Excréments noirs de 1-2cm", "Traces de grignotage sur câbles et bois", "Bruits de grattement la nuit", "Odeur musquée caractéristique"],
    risks: ["Transmission de maladies (leptospirose, salmonellose)", "Dégâts matériels (câbles électriques, isolation)", "Contamination alimentaire"],
    prevention: ["Boucher les ouvertures de plus de 6mm", "Ne pas laisser de nourriture accessible", "Maintenir une bonne hygiène", "Éliminer les points d'eau stagnante"]
  },
  {
    id: "cafards",
    name: "Cafards & Blattes",
    icon: Bug,
    description: "Les cafards sont des insectes nocturnes qui se reproduisent très rapidement.",
    signs: ["Présence d'insectes la nuit", "Excréments ressemblant à du marc de café", "Odeur désagréable", "Oothèques (poches d'œufs) dans les recoins"],
    risks: ["Allergies et asthme", "Transmission de bactéries", "Contamination alimentaire"],
    prevention: ["Nettoyer régulièrement cuisine et sanitaires", "Ne pas laisser de vaisselle sale", "Boucher les fissures", "Réparer les fuites d'eau"]
  },
  {
    id: "punaises",
    name: "Punaises de Lit",
    icon: Bug,
    description: "Les punaises de lit sont de petits insectes qui se nourrissent de sang humain pendant le sommeil.",
    signs: ["Piqûres en ligne sur la peau", "Petites taches de sang sur les draps", "Points noirs sur le matelas", "Insectes plats brunâtres de 5-7mm"],
    risks: ["Démangeaisons intenses", "Troubles du sommeil", "Stress et anxiété", "Infections secondaires par grattage"],
    prevention: ["Inspecter les bagages après un voyage", "Examiner les meubles d'occasion", "Laver le linge à 60°C minimum", "Aspirer régulièrement"]
  },
  {
    id: "guepes",
    name: "Guêpes & Frelons",
    icon: Bug,
    description: "Les guêpes et frelons construisent des nids pouvant abriter des milliers d'individus.",
    signs: ["Présence d'insectes jaune et noir", "Nid en papier mâché", "Activité intense autour d'un point fixe", "Bourdonnement caractéristique"],
    risks: ["Piqûres douloureuses", "Réactions allergiques graves (choc anaphylactique)", "Agressivité si le nid est dérangé"],
    prevention: ["Ne pas laisser de nourriture sucrée à l'extérieur", "Boucher les cavités dans les murs", "Faire inspecter les toitures au printemps", "Ne jamais intervenir soi-même sur un nid"]
  },
  {
    id: "pigeons",
    name: "Pigeons",
    icon: Bird,
    description: "Les pigeons causent des nuisances importantes par leurs fientes et leurs nids.",
    signs: ["Accumulation de fientes", "Présence de plumes et nids", "Dégradation des façades", "Bruit constant"],
    risks: ["Maladies respiratoires", "Détérioration des bâtiments", "Parasites (puces, tiques)", "Obstruction des gouttières"],
    prevention: ["Installer des pics ou filets", "Supprimer les sources de nourriture", "Boucher les accès aux toitures", "Nettoyage régulier des surfaces"]
  },
  {
    id: "chenilles",
    name: "Chenilles Processionnaires",
    icon: TreeDeciduous,
    description: "Les chenilles processionnaires sont très dangereuses pour l'homme et les animaux.",
    signs: ["Nids blancs soyeux dans les pins/chênes", "Processions de chenilles en file indienne", "Défoliation des arbres", "Poils urticants au sol"],
    risks: ["Réactions allergiques graves", "Problèmes respiratoires", "Lésions oculaires", "Danger mortel pour les animaux domestiques"],
    prevention: ["Installer des pièges à phéromones", "Poser des éco-pièges sur les troncs", "Faire intervenir un professionnel", "Éviter de toucher nids et chenilles"]
  }
];

// Intervention zones
const ZONES = [
  "Meaux", "Coulommiers", "Melun", "Marne-la-Vallée", "Créteil", 
  "Versailles", "Roissy-en-France", "Chelles", "Lagny-sur-Marne",
  "Claye-Souilly", "Val d'Europe", "Torcy", "Pontault-Combault",
  "Bussy-Saint-Georges", "Chessy", "Serris", "Senlis", "Crépy-en-Valois",
  "La Ferté-sous-Jouarre", "Lizy-sur-Ourcq", "Mary-sur-Marne"
];

// Process steps
const PROCESS = [
  {
    step: 1,
    title: "Diagnostic",
    description: "Inspection complète de votre site pour identifier la nature et l'étendue de l'infestation.",
    icon: Search
  },
  {
    step: 2,
    title: "Traitement",
    description: "Application de solutions adaptées avec des produits professionnels et techniques éprouvées.",
    icon: Wrench
  },
  {
    step: 3,
    title: "Prévention",
    description: "Conseils et mesures préventives pour éviter toute réinfestation future.",
    icon: CheckCircle
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    id: 1,
    name: "Fiona DELGADO",
    location: "Local Guide",
    rating: 5,
    text: "Hyper rapide et efficace !! En 10 min les guêpes ont disparus. Merci beaucoup !!",
    date: "Juillet 2025"
  },
  {
    id: 2,
    name: "Romain LUKOWSKI",
    location: "Client vérifié",
    rating: 5,
    text: "Professionnel très sérieux, je recommande !",
    date: "Juillet 2025"
  },
  {
    id: 3,
    name: "Sebastien GOURDAIN",
    location: "Local Guide",
    rating: 5,
    text: "Simplicité et efficacité.",
    date: "Juillet 2025"
  },
  {
    id: 4,
    name: "Francis Owie",
    location: "Client vérifié",
    rating: 5,
    text: "Très satisfait du travail effectué, équipe réactive et compétente.",
    date: "Avril 2021"
  }
];

// Gallery images
const GALLERY = [
  {
    url: "https://customer-assets.emergentagent.com/job_deratisation-expert/artifacts/7re74ovx_photo%20en%20haut%20du%20site.jpg",
    alt: "Technicien en intervention"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_deratisation-expert/artifacts/sow7x0jh_images%202.jpg",
    alt: "Véhicule d'intervention Hygiène de l'Ourcq"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_deratisation-expert/artifacts/5yc7t5nn_images%203.jpg",
    alt: "Intervention extérieure"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_deratisation-expert/artifacts/1esx92bv_IMAGE%204.jpg",
    alt: "Traitement sous meuble"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_deratisation-expert/artifacts/ypbclvs8_images%201.jpg",
    alt: "Pose de piège à rongeurs"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_deratisation-expert/artifacts/y4h5vole_image.png",
    alt: "Équipement professionnel"
  }
];

// Problems list for form
const PROBLEMS = [
  "Rats / Souris",
  "Cafards / Blattes",
  "Punaises de lit",
  "Guêpes / Frelons",
  "Fourmis",
  "Pigeons",
  "Taupes",
  "Chenilles processionnaires",
  "Désinfection",
  "Achat de produit",
  "Autre"
];

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#accueil", label: "Accueil" },
    { href: "#services", label: "Nos Services" },
    { href: "#nuisibles", label: "Les Nuisibles" },
    { href: "#zones", label: "Zones d'intervention" },
    { href: "#apropos", label: "À propos" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white shadow-lg" : "bg-white/95 backdrop-blur-sm"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#accueil" className="flex items-center" data-testid="logo-link">
            <img 
              src={COMPANY.logo} 
              alt={COMPANY.name}
              className="h-16 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="font-medium text-slate-700 hover:text-accent transition-colors relative group text-sm"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all group-hover:w-full"></span>
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center ml-6">
            <a 
              href={`tel:${COMPANY.phone2.replace(/\./g, '')}`}
              className="flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-6 py-3 rounded-md transition-all hover:shadow-lg active:scale-95"
              data-testid="header-cta-btn"
            >
              <Phone className="w-4 h-4" />
              <span>Urgence 24/7</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-slate-100 mobile-menu-enter" data-testid="mobile-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 font-medium text-slate-700 hover:text-accent transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a 
              href={`tel:${COMPANY.phone2.replace(/\./g, '')}`}
              className="flex items-center justify-center gap-2 mt-4 bg-accent text-white font-bold px-6 py-3 rounded-md"
            >
              <Phone className="w-4 h-4" />
              <span>Appeler maintenant</span>
            </a>
          </nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="accueil" 
      className="relative min-h-screen flex items-center pt-20 hero-pattern overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full">
              <Shield className="w-4 h-4" />
              <span className="text-sm font-semibold">Expert en hygiène depuis {COMPANY.since}</span>
            </div>
            
            <h1 className="font-chivo font-black text-4xl sm:text-5xl lg:text-6xl text-primary leading-tight tracking-tight">
              Entreprise de Dératisation et Désinsectisation
              <span className="text-accent"> Professionnelle</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
              Intervention rapide pour éliminer <strong>rats, souris, punaises de lit, cafards, frelons</strong> et autres nuisibles. 
              Devis gratuit, résultats garantis.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={`tel:${COMPANY.phone2.replace(/\./g, '')}`}
                className="btn-primary inline-flex items-center justify-center gap-3 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-md text-lg"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Appeler maintenant
              </a>
              <a 
                href="#contact"
                className="btn-secondary inline-flex items-center justify-center gap-3 bg-white border-2 border-primary text-primary hover:bg-slate-50 font-bold px-8 py-4 rounded-md text-lg"
                data-testid="hero-quote-btn"
              >
                Demander un devis
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-6 pt-4">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <span className="text-slate-600 text-sm">Avis clients</span>
              </div>
              <div className="h-8 w-px bg-slate-300"></div>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-accent" />
                <span className="text-slate-600 text-sm">Intervention 7j/7</span>
              </div>
              <div className="h-8 w-px bg-slate-300 hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-accent" />
                <span className="text-slate-600 text-sm">+35 ans d'expertise</span>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative hidden lg:block">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_deratisation-expert/artifacts/1esx92bv_IMAGE%204.jpg"
                alt="Technicien professionnel en intervention"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent"></div>
            </div>
            
            {/* Floating card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 animate-float">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-success-light rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-success" />
                </div>
                <div>
                  <p className="font-bold text-primary">Devis Gratuit</p>
                  <p className="text-sm text-slate-500">Sans engagement</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-indicator hidden md:block">
        <ChevronDown className="w-8 h-8 text-slate-400 animate-bounce" />
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className="py-20 md:py-32 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-4">
            Nos Services
          </span>
          <h2 className="font-chivo font-bold text-3xl md:text-4xl lg:text-5xl text-primary mb-6">
            Des Solutions Professionnelles pour Chaque Nuisible
          </h2>
          <p className="text-slate-600 text-lg">
            Dératisation, désinsectisation, désinfection : nous intervenons rapidement et efficacement 
            pour éliminer tous types de nuisibles de votre habitat ou local professionnel.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {SERVICES.map((service) => (
            <Card 
              key={service.id}
              className="service-card card-hover bg-white border border-slate-200 rounded-xl p-8 hover:border-accent/30 transition-all duration-300 group"
              data-testid={`service-card-${service.id}`}
            >
              <CardContent className="p-0">
                <div className="w-14 h-14 bg-accent/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-accent group-hover:text-white transition-all">
                  <service.icon className="w-7 h-7 text-accent group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-chivo font-bold text-xl text-primary mb-2">
                  {service.title}
                </h3>
                <p className="text-accent font-medium text-sm mb-3">{service.subtitle}</p>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <a 
                  href="#contact" 
                  className="inline-flex items-center gap-2 text-accent font-semibold hover:gap-3 transition-all"
                >
                  En savoir plus
                  <ArrowRight className="w-4 h-4" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pests Info Section - Educational
const PestsInfoSection = () => {
  const [selectedPest, setSelectedPest] = useState(null);

  return (
    <section id="nuisibles" className="py-20 md:py-32 bg-slate-50" data-testid="pests-info-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-4">
            Comprendre les Nuisibles
          </span>
          <h2 className="font-chivo font-bold text-3xl md:text-4xl lg:text-5xl text-primary mb-6">
            Identifiez Votre Problème
          </h2>
          <p className="text-slate-600 text-lg">
            Apprenez à reconnaître les signes d'infestation et les risques associés à chaque nuisible. 
            Cette information vous aidera à réagir rapidement.
          </p>
        </div>

        {/* Pest selector */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {PESTS_INFO.map((pest) => (
            <button
              key={pest.id}
              onClick={() => setSelectedPest(selectedPest?.id === pest.id ? null : pest)}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                selectedPest?.id === pest.id
                  ? "bg-accent text-white shadow-lg"
                  : "bg-white text-slate-700 border border-slate-200 hover:border-accent hover:text-accent"
              }`}
              data-testid={`pest-btn-${pest.id}`}
            >
              <pest.icon className="w-5 h-5" />
              {pest.name}
            </button>
          ))}
        </div>

        {/* Selected pest info */}
        {selectedPest && (
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-10 animate-in fade-in duration-300" data-testid="pest-details">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center">
                <selectedPest.icon className="w-8 h-8 text-accent" />
              </div>
              <div>
                <h3 className="font-chivo font-bold text-2xl text-primary">{selectedPest.name}</h3>
                <p className="text-slate-600">{selectedPest.description}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Signs */}
              <div className="bg-slate-50 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Search className="w-5 h-5 text-accent" />
                  <h4 className="font-chivo font-bold text-primary">Signes d'infestation</h4>
                </div>
                <ul className="space-y-2">
                  {selectedPest.signs.map((sign, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></span>
                      {sign}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Risks */}
              <div className="bg-red-50 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5 text-red-600" />
                  <h4 className="font-chivo font-bold text-red-900">Risques pour la santé</h4>
                </div>
                <ul className="space-y-2">
                  {selectedPest.risks.map((risk, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-red-800">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                      {risk}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Prevention */}
              <div className="bg-green-50 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h4 className="font-chivo font-bold text-green-900">Prévention</h4>
                </div>
                <ul className="space-y-2">
                  {selectedPest.prevention.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-green-800">
                      <span className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-slate-600 text-center sm:text-left">
                <strong>Vous avez identifié ces signes ?</strong> N'attendez pas, contactez-nous !
              </p>
              <a 
                href="#contact"
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-6 py-3 rounded-md transition-all"
              >
                <Phone className="w-4 h-4" />
                Demander une intervention
              </a>
            </div>
          </div>
        )}

        {/* Default message when no pest selected */}
        {!selectedPest && (
          <div className="text-center py-12 bg-white rounded-2xl border-2 border-dashed border-slate-200">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Cliquez sur un nuisible ci-dessus pour en savoir plus</p>
          </div>
        )}
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyUsSection = () => {
  return (
    <section id="apropos" className="py-20 md:py-32 bg-slate-50" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <div>
            <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-4">
              Pourquoi Nous Choisir
            </span>
            <h2 className="font-chivo font-bold text-3xl md:text-4xl lg:text-5xl text-primary mb-6">
              Votre Expert Anti-Nuisibles depuis {COMPANY.since}
            </h2>
            <p className="text-slate-600 text-lg mb-8 leading-relaxed">
              <strong>{COMPANY.name}</strong>, dirigée par <strong>{COMPANY.director}</strong>, 
              met son expertise de plus de 35 ans au service des particuliers et professionnels 
              de Seine-et-Marne et d'Île-de-France.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {WHY_US.map((item, index) => (
                <div 
                  key={index} 
                  className="flex gap-4 p-4 bg-white rounded-lg border border-slate-100 hover:shadow-md transition-shadow"
                  data-testid={`why-us-item-${index}`}
                >
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-chivo font-bold text-primary mb-1">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Image / Info card */}
          <div className="relative">
            <div className="bg-primary rounded-2xl p-8 md:p-12 text-white">
              <h3 className="font-chivo font-bold text-2xl mb-6">Nos Domaines d'Expertise</h3>
              <ul className="space-y-4">
                {["Désinsectisation", "Dératisation - Désourisation", "Désinfection", "Dépigeonnisation", "Vente de produits", "Taupes", "Débarras"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-8 border-t border-white/20">
                <p className="text-white/80 text-sm mb-2">SIREN</p>
                <p className="font-mono">{COMPANY.siren}</p>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -top-4 -right-4 bg-accent text-white rounded-full w-24 h-24 flex flex-col items-center justify-center shadow-xl">
              <span className="text-3xl font-chivo font-black">35+</span>
              <span className="text-xs">ans</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Zones Section
const ZonesSection = () => {
  return (
    <section id="zones" className="py-20 md:py-32 bg-white" data-testid="zones-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-4">
            Zones d'Intervention
          </span>
          <h2 className="font-chivo font-bold text-3xl md:text-4xl lg:text-5xl text-primary mb-6">
            Nous Intervenons en Seine-et-Marne et Île-de-France
          </h2>
          <p className="text-slate-600 text-lg">
            Intervention rapide dans tout le département 77 et les départements limitrophes. 
            Contactez-nous pour vérifier notre disponibilité dans votre secteur.
          </p>
        </div>

        {/* Zones tags */}
        <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
          {ZONES.map((zone, index) => (
            <span 
              key={index}
              className="zone-tag px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-medium cursor-default hover:bg-accent hover:text-white"
              data-testid={`zone-tag-${index}`}
            >
              {zone}
            </span>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center">
          <p className="text-slate-600 mb-4">Votre ville n'est pas listée ?</p>
          <a 
            href={`tel:${COMPANY.phone2.replace(/\./g, '')}`}
            className="inline-flex items-center gap-2 text-accent font-semibold hover:underline"
          >
            <Phone className="w-4 h-4" />
            Contactez-nous pour vérifier
          </a>
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  return (
    <section className="py-20 md:py-32 bg-slate-50" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-4">
            Notre Processus
          </span>
          <h2 className="font-chivo font-bold text-3xl md:text-4xl lg:text-5xl text-primary mb-6">
            Une Intervention en 3 Étapes
          </h2>
          <p className="text-slate-600 text-lg">
            Notre méthode éprouvée garantit une élimination efficace et durable des nuisibles.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 md:gap-12">
          {PROCESS.map((item, index) => (
            <div 
              key={item.step} 
              className={`relative text-center ${index < PROCESS.length - 1 ? 'step-connector' : ''}`}
              data-testid={`process-step-${item.step}`}
            >
              <div className="step-number w-20 h-20 bg-accent text-white rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="font-chivo font-black text-3xl">{item.step}</span>
              </div>
              <div className="w-14 h-14 bg-white border-2 border-slate-200 rounded-lg flex items-center justify-center mx-auto mb-4">
                <item.icon className="w-7 h-7 text-accent" />
              </div>
              <h3 className="font-chivo font-bold text-xl text-primary mb-3">{item.title}</h3>
              <p className="text-slate-600">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section className="py-20 md:py-32 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-4">
            Témoignages
          </span>
          <h2 className="font-chivo font-bold text-3xl md:text-4xl lg:text-5xl text-primary mb-6">
            Ce que Disent Nos Clients
          </h2>
          <p className="text-slate-600 text-lg">
            La satisfaction de nos clients est notre priorité. Découvrez leurs retours d'expérience.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {TESTIMONIALS.map((testimonial) => (
            <div 
              key={testimonial.id}
              className="testimonial-card bg-slate-50 border border-slate-100 rounded-xl p-6 md:p-8"
              data-testid={`testimonial-${testimonial.id}`}
            >
              <div className="flex mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-slate-700 mb-6 italic leading-relaxed">"{testimonial.text}"</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                    <span className="font-bold text-accent">{testimonial.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-primary">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.location}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-400">{testimonial.date}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  return (
    <section id="galerie" className="py-20 md:py-32 bg-slate-50" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-4">
            Galerie
          </span>
          <h2 className="font-chivo font-bold text-3xl md:text-4xl lg:text-5xl text-primary mb-6">
            Nos Interventions
          </h2>
          <p className="text-slate-600 text-lg">
            Découvrez notre équipement professionnel et nos interventions sur le terrain.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {GALLERY.map((image, index) => (
            <div 
              key={index}
              className="gallery-image rounded-lg overflow-hidden aspect-square"
              data-testid={`gallery-image-${index}`}
            >
              <img 
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    noms: '',
    email: '',
    numero: '',
    ou: '',
    probleme: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, probleme: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(STATICFORMS_CONFIG.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accessKey: STATICFORMS_CONFIG.accessKey,
          name: formData.noms,
          email: formData.email,
          phone: formData.numero,
          subject: `Demande: ${formData.probleme} - ${formData.ou}`,
          message: `Nom: ${formData.noms}\nEmail: ${formData.email}\nTéléphone: ${formData.numero}\nLieu: ${formData.ou}\nProblème: ${formData.probleme}\n\nMessage:\n${formData.message}`,
          replyTo: formData.email,
          redirectTo: ''
        })
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success("Message envoyé avec succès ! Nous vous recontacterons rapidement.");
        setFormData({
          noms: '',
          email: '',
          numero: '',
          ou: '',
          probleme: '',
          message: ''
        });
      } else {
        throw new Error(result.message || "Erreur d'envoi");
      }
    } catch (error) {
      console.error("StaticForms Error:", error);
      toast.error("Une erreur est survenue. Veuillez nous appeler directement.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-4">
            Contact
          </span>
          <h2 className="font-chivo font-bold text-3xl md:text-4xl lg:text-5xl text-primary mb-6">
            Demandez Votre Devis Gratuit
          </h2>
          <p className="text-slate-600 text-lg">
            Contactez-nous par téléphone ou remplissez le formulaire. Nous vous répondons sous 24h.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-slate-50 rounded-xl p-6 md:p-8">
              <h3 className="font-chivo font-bold text-xl text-primary mb-6">Nos Coordonnées</h3>
              
              <div className="space-y-5">
                <a 
                  href={`tel:${COMPANY.phone1.replace(/\./g, '')}`}
                  className="flex items-center gap-4 p-3 bg-white rounded-lg hover:shadow-md transition-shadow"
                  data-testid="contact-phone-1"
                >
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Téléphone fixe</p>
                    <p className="font-semibold text-primary">{COMPANY.phone1}</p>
                  </div>
                </a>

                <a 
                  href={`tel:${COMPANY.phone2.replace(/\./g, '')}`}
                  className="flex items-center gap-4 p-3 bg-white rounded-lg hover:shadow-md transition-shadow"
                  data-testid="contact-phone-2"
                >
                  <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Mobile / Urgences</p>
                    <p className="font-bold text-accent text-lg">{COMPANY.phone2}</p>
                  </div>
                </a>

                <a 
                  href={`mailto:${COMPANY.email}`}
                  className="flex items-center gap-4 p-3 bg-white rounded-lg hover:shadow-md transition-shadow"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-semibold text-primary">{COMPANY.email}</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-3 bg-white rounded-lg">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Adresse</p>
                    <p className="font-semibold text-primary">{COMPANY.address}</p>
                    <p className="text-slate-600">{COMPANY.city}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-3 bg-white rounded-lg">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Horaires</p>
                    <p className="font-semibold text-primary">Lun - Sam : 8h - 20h</p>
                    <p className="text-accent font-medium">Urgences 7j/7</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick call CTA */}
            <div className="bg-accent rounded-xl p-6 md:p-8 text-white text-center">
              <h3 className="font-chivo font-bold text-xl mb-3">Besoin d'une Intervention Urgente ?</h3>
              <p className="text-white/80 mb-4">Appelez-nous directement pour une prise en charge immédiate</p>
              <a 
                href={`tel:${COMPANY.phone2.replace(/\./g, '')}`}
                className="inline-flex items-center gap-2 bg-white text-accent font-bold px-8 py-3 rounded-md hover:bg-slate-100 transition-colors"
                data-testid="contact-urgent-call-btn"
              >
                <Phone className="w-5 h-5" />
                {COMPANY.phone2}
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-50 rounded-xl p-6 md:p-8">
            <h3 className="font-chivo font-bold text-xl text-primary mb-6">Formulaire de Contact</h3>
            
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div>
                <label htmlFor="noms" className="block text-sm font-medium text-slate-700 mb-2">
                  Noms *
                </label>
                <Input
                  id="noms"
                  name="noms"
                  type="text"
                  required
                  value={formData.noms}
                  onChange={handleChange}
                  placeholder="Votre nom complet"
                  className="form-input w-full h-12 border-slate-200 focus:border-accent focus:ring-accent"
                  data-testid="form-noms"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre@email.com"
                    className="form-input w-full h-12 border-slate-200 focus:border-accent focus:ring-accent"
                    data-testid="form-email"
                  />
                </div>
                <div>
                  <label htmlFor="numero" className="block text-sm font-medium text-slate-700 mb-2">
                    Numéro *
                  </label>
                  <Input
                    id="numero"
                    name="numero"
                    type="tel"
                    required
                    value={formData.numero}
                    onChange={handleChange}
                    placeholder="06 XX XX XX XX"
                    className="form-input w-full h-12 border-slate-200 focus:border-accent focus:ring-accent"
                    data-testid="form-numero"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="ou" className="block text-sm font-medium text-slate-700 mb-2">
                  Où (Ville / Adresse) *
                </label>
                <Input
                  id="ou"
                  name="ou"
                  type="text"
                  required
                  value={formData.ou}
                  onChange={handleChange}
                  placeholder="Votre ville ou adresse d'intervention"
                  className="form-input w-full h-12 border-slate-200 focus:border-accent focus:ring-accent"
                  data-testid="form-ou"
                />
              </div>

              <div>
                <label htmlFor="probleme" className="block text-sm font-medium text-slate-700 mb-2">
                  Type de Problème *
                </label>
                <Select value={formData.probleme} onValueChange={handleSelectChange} required>
                  <SelectTrigger className="w-full h-12 border-slate-200 focus:border-accent focus:ring-accent" data-testid="form-probleme">
                    <SelectValue placeholder="Sélectionnez un type de nuisible" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROBLEMS.map((problem) => (
                      <SelectItem key={problem} value={problem}>
                        {problem}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Décrivez votre problème en détail..."
                  className="form-input w-full border-slate-200 focus:border-accent focus:ring-accent resize-none"
                  data-testid="form-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent hover:bg-accent-hover text-white font-bold py-4 h-auto text-lg"
                data-testid="form-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Envoi en cours...
                  </span>
                ) : (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    Envoyer ma demande
                  </>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                En soumettant ce formulaire, vous acceptez d'être contacté par {COMPANY.name}.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company info */}
          <div className="space-y-4">
            <img 
              src={COMPANY.logo} 
              alt={COMPANY.name}
              className="h-16 w-auto object-contain bg-white rounded p-2"
            />
            <p className="text-slate-300 text-sm leading-relaxed">
              Expert en dératisation, désinsectisation et désinfection depuis {COMPANY.since} en Seine-et-Marne et Île-de-France.
            </p>
            <div className="flex gap-3">
              <a href="#" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-accent transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-accent transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-accent transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-chivo font-bold text-lg mb-4">Nos Services</h4>
            <ul className="space-y-2">
              {["Dératisation", "Désinsectisation", "Guêpes & Frelons", "Désinfection", "Chenilles Processionnaires", "Anti-Pigeons", "Vente de Produits"].map((service) => (
                <li key={service}>
                  <a href="#services" className="text-slate-300 hover:text-white transition-colors text-sm">
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-chivo font-bold text-lg mb-4">Liens Rapides</h4>
            <ul className="space-y-2">
              {[
                { href: "#accueil", label: "Accueil" },
                { href: "#services", label: "Services" },
                { href: "#zones", label: "Zones d'intervention" },
                { href: "#apropos", label: "À propos" },
                { href: "#galerie", label: "Galerie" },
                { href: "#contact", label: "Contact" }
              ].map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="text-slate-300 hover:text-white transition-colors text-sm">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-chivo font-bold text-lg mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <div>
                  <a href={`tel:${COMPANY.phone1.replace(/\./g, '')}`} className="text-slate-300 hover:text-white block text-sm">{COMPANY.phone1}</a>
                  <a href={`tel:${COMPANY.phone2.replace(/\./g, '')}`} className="text-white font-semibold block">{COMPANY.phone2}</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <a href={`mailto:${COMPANY.email}`} className="text-slate-300 hover:text-white text-sm break-all">{COMPANY.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <div className="text-slate-300 text-sm">
                  <p>{COMPANY.address}</p>
                  <p>{COMPANY.city}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 text-sm text-center md:text-left">
            © {currentYear} {COMPANY.name}. Tous droits réservés.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Mentions légales</a>
            <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Politique de confidentialité</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Floating Call Button
const FloatingCallButton = () => {
  return (
    <a
      href={`tel:${COMPANY.phone2.replace(/\./g, '')}`}
      className="fixed bottom-6 right-6 z-[9999] bg-accent text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform md:hidden flex items-center justify-center"
      style={{ width: '60px', height: '60px' }}
      aria-label="Appeler maintenant"
      data-testid="floating-call-btn"
    >
      <Phone className="w-6 h-6" />
      <span className="absolute -inset-1 rounded-full bg-accent/40 animate-ping"></span>
    </a>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <PestsInfoSection />
        <WhyUsSection />
        <ZonesSection />
        <ProcessSection />
        <TestimonialsSection />
        <GallerySection />
        <ContactSection />
      </main>
      <Footer />
      <FloatingCallButton />
    </div>
  );
}

export default App;
